"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMutation } from "@tanstack/react-query";
import {
  removeAvatar,
  removeOrgLogo,
  uploadAvatar,
  uploadOrgLogo,
  type UploadAvatarMutationResult,
  type UploadOrgLogoMutationResult,
} from "@/lib/api/generated";
import { AvatarCropDialog } from "@/components/ui/avatar-crop-dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Icon, type IconName } from "@/components/ui/icon";
import { SubmitButton } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface AvatarUploadProps {
  imageUrl: string | null;
  onImageChange: (url: string) => void;
  onImageRemove: () => void;
  label?: string;
  optional?: boolean;
  placeholderIcon?: IconName;
  uploadKind?: "avatar" | "org-logo";
  /**
   * Custom upload function. When provided, it is used instead of the built-in
   * `uploadAvatar`/`uploadOrgLogo` mutations and the returned string is the URL
   * handed to `onImageChange`. Lets callers target arbitrary uploads (e.g. a
   * signature asset) without this `ui/` primitive importing feature code.
   */
  uploadFn?: (file: File) => Promise<string>;
  /**
   * Custom removal handler. When `uploadFn` is set, removal never hits a server
   * delete endpoint — it clears locally, calls `onImageRemove`, and (if given)
   * awaits `onRemoveFn`.
   */
  onRemoveFn?: () => void | Promise<void>;
  /** Crop mask shape; also drives the preview thumbnail shape. */
  cropShape?: "round" | "rect";
  /** Crop box aspect ratio (width / height), passed through to the cropper. */
  aspect?: number;
  /**
   * Width in px of the cropped JPEG; the height follows `aspect`. Defaults to
   * 400, so a square caller is unchanged — but a wide `aspect` needs it, or the
   * crop comes back 400 px wide and only `400 / aspect` tall.
   */
  outputWidth?: number;
  /**
   * Skip the crop step entirely. When true, selecting a file uploads the
   * original image directly via `uploadFn`/the built-in mutation WITHOUT opening
   * `AvatarCropDialog`, preserving the source aspect ratio and file type (e.g. a
   * wide transparent PNG wordmark used as a logo).
   */
  disableCrop?: boolean;
  /** Override the file input `accept` attribute. */
  accept?: string;
  /**
   * Dense variant for config sidebars (e.g. the signature builder): smaller
   * thumbnail, quiet secondary buttons instead of primary, single-line hint.
   * The default layout is unchanged for existing callers.
   */
  layout?: "default" | "compact";
}

type ApiData<T> = T extends { data: infer D } ? D : T;
type UploadResult = ApiData<
  UploadAvatarMutationResult | UploadOrgLogoMutationResult
>;

export function AvatarUpload({
  imageUrl,
  onImageChange,
  onImageRemove,
  label = "Profile Picture",
  optional = false,
  placeholderIcon = "IconPeopleCircle",
  uploadKind = "avatar",
  uploadFn,
  onRemoveFn,
  cropShape = "round",
  aspect = 1,
  outputWidth = 400,
  disableCrop = false,
  accept = "image/png,image/jpeg",
  layout = "default",
}: AvatarUploadProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(imageUrl);
  const [hadExistingImageBeforeUpload, setHadExistingImageBeforeUpload] =
    useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);

  useEffect(() => {
    setPreviewUrl(imageUrl);
  }, [imageUrl]);

  const uploadMutation = useMutation({
    mutationFn: async (blob: Blob): Promise<UploadResult> => {
      // Cropped output arrives as a bare JPEG Blob; the no-crop path passes the
      // original `File` through untouched so its type/extension (e.g. a
      // transparent PNG) survives the upload.
      const file =
        blob instanceof File
          ? blob
          : new File([blob], "avatar.jpg", { type: "image/jpeg" });
      if (uploadFn) {
        const url = await uploadFn(file);
        return { url } as UploadResult;
      }
      if (uploadKind === "org-logo") {
        return uploadOrgLogo({ file }) as Promise<UploadResult>;
      }
      return uploadAvatar({ file }) as Promise<UploadResult>;
    },
    onMutate: (blob) => {
      const localPreview = URL.createObjectURL(blob);
      setPreviewUrl(localPreview);
      return { localPreview };
    },
    onSuccess: (result, _blob, context) => {
      if (context?.localPreview) URL.revokeObjectURL(context.localPreview);
      const nextUrl = (result as { url?: string }).url;
      if (!nextUrl) return;
      setPreviewUrl(nextUrl);
      onImageChange(nextUrl);
    },
    onError: (_err, _blob, context) => {
      if (context?.localPreview) URL.revokeObjectURL(context.localPreview);
      setPreviewUrl(imageUrl);
    },
    onSettled: () => setHadExistingImageBeforeUpload(false),
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      // Custom-upload callers (e.g. signature assets) have no server delete
      // endpoint — removal is purely local + an optional caller hook.
      if (uploadFn) {
        await onRemoveFn?.();
        return undefined;
      }
      if (uploadKind === "org-logo") {
        return removeOrgLogo();
      }
      return removeAvatar();
    },
    onMutate: () => {
      const previousPreview = previewUrl;
      setPreviewUrl(null);
      if (fileRef.current) fileRef.current.value = "";
      // Local-only custom uploads: clear form state immediately.
      // Server-backed removes wait for onSuccess so auth/org cache invalidation
      // does not race a /me refetch that still returns the old image URL.
      if (uploadFn) onImageRemove();
      return { previousPreview };
    },
    onSuccess: () => {
      if (!uploadFn) onImageRemove();
    },
    onError: (_err, _vars, context) => {
      setPreviewUrl(context?.previousPreview ?? imageUrl);
    },
  });

  const isUploading = uploadMutation.isPending;
  const isRemoving = removeMutation.isPending;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setHadExistingImageBeforeUpload(!!previewUrl);

    if (disableCrop) {
      // No crop dialog: upload the original file directly, preserving aspect.
      if (fileRef.current) fileRef.current.value = "";
      uploadMutation.mutate(file);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setCropSrc(objectUrl);
    setShowCropDialog(true);

    if (fileRef.current) fileRef.current.value = "";
  };

  const handleCrop = useCallback(
    (blob: Blob) => {
      setShowCropDialog(false);
      if (cropSrc) {
        URL.revokeObjectURL(cropSrc);
        setCropSrc(null);
      }
      uploadMutation.mutate(blob);
    },
    [cropSrc, uploadMutation],
  );

  const handleCropCancel = useCallback(() => {
    setShowCropDialog(false);
    setHadExistingImageBeforeUpload(false);
    if (cropSrc) {
      URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    }
  }, [cropSrc]);

  const handleRemove = () => {
    removeMutation.mutate();
  };

  const showExistingImageActions =
    !!previewUrl && (!isUploading || hadExistingImageBeforeUpload);

  const isRound = cropShape === "round";
  const compact = layout === "compact";
  const placeholderIconSize = compact ? 22 : 44;

  return (
    <>
      <div className={cn("flex items-center", compact ? "gap-3" : "gap-5")}>
        <div
          className={cn(
            "bg-contrast/6 overflow-hidden shrink-0 shadow-[0px_0.914px_1.829px_0px_rgba(0,0,0,0.05)] flex items-center justify-center",
            compact ? "size-12" : "size-20",
            isRound ? "rounded-full" : "rounded-md",
          )}
        >
          <AnimatePresence mode="wait">
            {isUploading ? (
              <motion.div
                key="loader"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <Icon
                  name="IconLoadingCircle"
                  size={24}
                  className="animate-spin text-heading opacity-40"
                />
              </motion.div>
            ) : previewUrl ? (
              <motion.div
                key="image"
                className="size-full"
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <Avatar
                  className={cn(
                    "size-full",
                    isRound ? "rounded-full" : "rounded-md",
                  )}
                >
                  <AvatarImage src={previewUrl} alt="" />
                  <AvatarFallback>
                    <Icon
                      name={placeholderIcon}
                      size={placeholderIconSize}
                      className="text-heading opacity-20"
                    />
                  </AvatarFallback>
                </Avatar>
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                className="size-full flex items-center justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <Icon
                  name={placeholderIcon}
                  size={placeholderIconSize}
                  className="text-heading opacity-20"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div
          className={cn(
            "flex flex-col flex-1 min-w-0",
            compact ? "gap-1.5" : "gap-2",
          )}
        >
          {/* Compact callers live inside a titled section, so repeating the
              label here would be redundant. */}
          {!compact && (
            <span className="text-sm font-normal text-heading inline-flex flex-wrap items-center gap-x-1">
              {label}
              {optional && (
                <span className="text-muted-foreground">(optional)</span>
              )}
            </span>
          )}
          <AnimatePresence mode="wait">
            {showExistingImageActions ? (
              <motion.div
                key="actions"
                className="flex flex-wrap items-center gap-x-3 gap-y-2"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
              >
                <SubmitButton
                  type="button"
                  size="sm"
                  variant={compact ? "secondary" : "primary"}
                  loading={isUploading}
                  loadingLabel="Uploading"
                  disabled={isRemoving}
                  icon={<Icon name="IconCloudUpload" size={compact ? 14 : 16} />}
                  onClick={() => fileRef.current?.click()}
                >
                  {compact ? "Replace" : "Replace Image"}
                </SubmitButton>
                <SubmitButton
                  type="button"
                  size="sm"
                  variant="secondary"
                  loading={isRemoving}
                  loadingLabel="Removing"
                  disabled={isUploading}
                  onClick={handleRemove}
                >
                  Remove
                </SubmitButton>
              </motion.div>
            ) : (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
              >
                <SubmitButton
                  type="button"
                  size="sm"
                  variant={compact ? "secondary" : "primary"}
                  loading={isUploading}
                  loadingLabel="Uploading"
                  disabled={isRemoving}
                  icon={<Icon name="IconCloudUpload" size={compact ? 14 : 16} />}
                  onClick={() => !isUploading && fileRef.current?.click()}
                >
                  {compact ? "Upload" : "Upload Image"}
                </SubmitButton>
              </motion.div>
            )}
          </AnimatePresence>
          <span className="text-xs text-contrast/40">
            {compact
              ? "PNG or JPEG, up to 10MB"
              : "*png, *jpeg files up to 10MB at least 400px by 400px"}
          </span>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>

      <AvatarCropDialog
        open={showCropDialog}
        imageSrc={cropSrc}
        cropShape={cropShape}
        aspect={aspect}
        outputWidth={outputWidth}
        onCrop={handleCrop}
        onCancel={handleCropCancel}
      />
    </>
  );
}
